import { clsx } from "clsx";
import { Check } from "lucide-react";

import type { EventFragment } from "~/__generated__/graphql/graphql";
import { centsToMoney } from "~/utils/money";
import { useRootLocale } from "~/root";
import {
  getSelectedTicketVariation,
  getTicketVariationAvailabilityLabel,
  getVisibleTicketVariations,
} from "./ticketVariations";

type Props = {
  plan: EventFragment;
  enabled: boolean;
  selectedVariationID: string | null;
  onSelect: (variationID: string) => void;
};

export function TicketVariationSelector({
  plan,
  enabled,
  selectedVariationID,
  onSelect,
}: Props) {
  const locale = useRootLocale();

  if (!enabled) return null;

  const variations = getVisibleTicketVariations(plan);
  const selected = getSelectedTicketVariation(plan, selectedVariationID);

  return (
    <div
      className="flex flex-col gap-3"
      role="radiogroup"
      aria-label="Ticket options"
    >
      {variations.map((variation) => {
        const isSelected = variation.id === selected?.id;
        const availability = getTicketVariationAvailabilityLabel(variation);
        const soldOut = availability === "Sold out";

        return (
          <button
            key={variation.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={!variation.available}
            onClick={() => onSelect(variation.id)}
            className={clsx(
              "flex w-full items-center gap-3 rounded-xl border border-transparent p-[0.6rem] text-left transition-colors focus-visible:border-butter-green sm:p-[0.8rem]",
              isSelected
                ? "bg-butter-green text-butter-cream-light"
                : "bg-white text-butter-green",
              variation.available
                ? "cursor-pointer"
                : "cursor-not-allowed opacity-50",
            )}
          >
            <span
              aria-hidden
              className="relative flex size-11 shrink-0 items-center justify-center rounded-lg bg-butter-cream text-xl"
            >
              {variation.emoji}
              {isSelected && variations.length > 1 && (
                <span className="absolute -right-1 -bottom-1 flex size-4 items-center justify-center rounded-full bg-butter-cream-light text-butter-green ring-2 ring-butter-green">
                  <Check className="size-3.5" strokeWidth={3} />
                </span>
              )}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-sans text-base font-bold wrap-break-word">
                {variation.name}
              </span>
              {variation.description && (
                <span className="mt-0.5 block font-sans text-sm leading-snug">
                  {variation.description}
                </span>
              )}
              {availability && !soldOut && (
                <span className="mt-1 block font-sans text-sm font-normal text-butter-pink">
                  {availability}
                </span>
              )}
            </span>
            <span className={clsx("shrink-0", soldOut ? "font-sans text-sm" : "font-serif text-base leading-tight font-bold")}>
              {soldOut ? "Sold out" : centsToMoney(variation.totalCostCents, "$", locale)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
